import PropTypes from "prop-types"
import React, { useEffect, useRef } from "react"

import { decodeBase64, decodeWindows1252 } from "./windows1252Utils"

export const parseReceipt = (bytes) => {
  const ops = []
  let i = 0
  let state = { align: "left", bold: false, underline: false }
  let imgCtx = null

  const flushImage = () => {
    if (!imgCtx) return
    const { rows, width } = imgCtx
    const height = rows.length
    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext("2d")
    const imgData = ctx.createImageData(width, height)
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4
        const v = rows[y][x] ? 0 : 255
        imgData.data[idx] = v
        imgData.data[idx + 1] = v
        imgData.data[idx + 2] = v
        imgData.data[idx + 3] = 255
      }
    }
    ctx.putImageData(imgData, 0, 0)
    ops.push({ type: "image", dataUrl: canvas.toDataURL(), width, height })
    imgCtx = null
  }

  while (i < bytes.length) {
    // QR‐block: ESC GS S m xL xH yL yH n
    if (bytes[i] === 0x1b && bytes[i + 1] === 0x1d && bytes[i + 2] === 0x53) {
      flushImage()
      const xL = bytes[i + 4]
      const xH = bytes[i + 5]
      const yL = bytes[i + 6]
      const yH = bytes[i + 7]
      const bytesPerLine = xL | (xH << 8)
      const widthPx = bytesPerLine * 8
      const heightPx = yL | (yH << 8)

      imgCtx = { rows: [], width: widthPx }

      const dataStart = i + 9
      const sliceSize = bytesPerLine * heightPx
      const slice = bytes.slice(dataStart, dataStart + sliceSize)

      for (let row = 0; row < heightPx; row++) {
        const off = row * bytesPerLine
        const rowArr = []
        for (let bit = 0; bit < widthPx; bit++) {
          const byte = slice[off + (bit >> 3)] || 0
          rowArr.push((byte >> (7 - (bit & 7))) & 1)
        }
        imgCtx.rows.push(rowArr)
      }

      i = dataStart + sliceSize
      continue
    }

    const b = bytes[i]
    if (b === 0x1b) {
      const next = bytes[i + 1]
      // skip RS/FS or ESC s "00"
      if (next === 0x1e || next === 0x1c || next === 0x73) {
        flushImage()
        i += 4
        continue
      }
      // graphic bit image (ESC k nL nH)
      if (next === 0x6b) {
        flushImage()
        imgCtx = { rows: [], width: 0 }
        const nL = bytes[i + 2]
        const nH = bytes[i + 3]
        const bpl = nL | (nH << 8)
        imgCtx.width = bpl * 8
        const dataStart = i + 4
        const sliceSize = bpl * 24
        const slice = bytes.slice(dataStart, dataStart + sliceSize)
        for (let row = 0; row < 24; row++) {
          const off = row * bpl
          const rowArr = []
          for (let bit = 0; bit < imgCtx.width; bit++) {
            const byte = slice[off + (bit >> 3)] || 0
            rowArr.push((byte >> (7 - (bit & 7))) & 1)
          }
          imgCtx.rows.push(rowArr)
        }
        i = dataStart + sliceSize
        if (bytes[i] === 0x0a) i++
        continue
      }
      // alignment: ESC GS a n
      if (next === 0x1d && bytes[i + 2] === 0x61) {
        flushImage()
        state.align = ["left", "center", "right"][bytes[i + 3]] || "left"
        i += 4
        continue
      }
      // bold: ESC GS E n
      if (next === 0x1d && bytes[i + 2] === 0x45) {
        state.bold = bytes[i + 3] === 1
        i += 4
        continue
      }
      // underline: ESC - n
      if (next === 0x2d) {
        state.underline = bytes[i + 2] === 1
        i += 3
        continue
      }
      // reset styles: ESC @
      if (next === 0x40) {
        flushImage()
        state = { align: "left", bold: false, underline: false }
        i += 2
        continue
      }
      // reset spacing: ESC 0
      if (next === 0x30) {
        flushImage()
        i += 2
        continue
      }
      // codepage select: just skip those 4 control bytes
      if (bytes[i + 1] === 0x1d && bytes[i + 2] === 0x74) {
        i += 4
        continue
      }
      i += 2
      continue
    }

    // line feed - only create line break for consecutive LFs or isolated LFs
    if (b === 0x0a) {
      // Look ahead to see if this is multiple line feeds (intentional spacing)
      let consecutiveLFs = 1
      let j = i + 1
      while (j < bytes.length && bytes[j] === 0x0a) {
        consecutiveLFs++
        j++
      }

      // Create linebreaks for multiple consecutive LFs (empty lines)
      // Each LF after the first creates one empty line
      if (consecutiveLFs > 1) {
        for (let k = 0; k < consecutiveLFs - 1; k++) {
          ops.push({ type: "linebreak" })
        }
      }

      i = j // Skip all the consecutive LFs
      continue
    }

    // other control codes
    if (b < 0x20) {
      i++
      continue
    }

    // text run
    let j = i
    while (j < bytes.length && bytes[j] >= 0x20) j++
    const content = decodeWindows1252(bytes.slice(i, j))
    if (content) {
      ops.push({ type: "text", content, ...state })
    }
    i = j
  }

  flushImage()
  return ops
}

const ReceiptPreview = ({
  base64,
  lineHeight = 22,
  font = "18px monospace",
}) => {
  const canvasRef = useRef(null)

  useEffect(() => {
    if (!base64) return

    const bytes = decodeBase64(base64)
    const ops = parseReceipt(bytes)

    // measure required width (widest op)
    const measure = document.createElement("canvas")
    const mctx = measure.getContext("2d")
    mctx.font = font
    let maxW = 0
    ops.forEach((op) => {
      if (op.type === "text") {
        const w = mctx.measureText(op.content).width
        maxW = Math.max(maxW, w)
      } else if (op.type === "linebreak") {
        // Linebreaks don't contribute to width
      } else {
        maxW = Math.max(maxW, op.width)
      }
    })

    const canvas = canvasRef.current
    canvas.width = Math.ceil(maxW)
    canvas.height = ops.reduce((sum, op) => {
      if (op.type === "text") return sum + lineHeight
      if (op.type === "linebreak") return sum + lineHeight * 0.5
      return sum + op.height
    }, 0)
    canvas.style.width = "100%"
    canvas.style.height = "auto"

    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.textBaseline = "top"

    let y = 0
    ops.forEach((op) => {
      if (op.type === "text") {
        ctx.font = `${op.bold ? "bold " : ""}${font}`
        const w = ctx.measureText(op.content).width
        let x = 0
        if (op.align === "center") x = (canvas.width - w) / 2
        else if (op.align === "right") x = canvas.width - w
        ctx.fillText(op.content, x, y)
        if (op.underline) {
          const th = parseInt(font, 10)
          ctx.beginPath()
          ctx.moveTo(x, y + th)
          ctx.lineTo(x + w, y + th)
          ctx.lineWidth = 1
          ctx.stroke()
        }
        y += lineHeight
      } else if (op.type === "linebreak") {
        y += lineHeight * 0.5 // Half line spacing for line feeds
      } else {
        const yPos = y
        const img = new Image()
        img.onload = () => {
          const x = (canvas.width - op.width) / 2
          ctx.drawImage(img, x, yPos)
        }
        img.src = op.dataUrl
        y += op.height
      }
    })
  }, [base64, lineHeight, font])

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: "100%",
        height: "auto",
        display: "block",
        padding: "30px 10px",
      }}
    />
  )
}

ReceiptPreview.propTypes = {
  base64: PropTypes.string.isRequired,
  lineHeight: PropTypes.number,
  font: PropTypes.string,
}

export default ReceiptPreview
